/**
 * ProjectileShapes — Reusable shape builders for projectiles and VFX.
 *
 * Each builder draws a fully-composed projectile (glow + inner + core +
 * highlight layers) onto a single IGraphics handle. Position + rotation
 * are owned by the caller (ProjectileRenderer) — the builders just paint
 * relative to (0, 0).
 *
 * Phase H-B Stage 5h: migrated from raw Phaser containers + scene.add.*
 * primitives to the substrate-agnostic IGraphics. Pixi/Phaser parity
 * comes for free since IGraphics already abstracts both.
 */

import type { IGraphics } from '../IRenderer';

// ── Defaults & Scale Constants ───────────────────────────────────

const SHAPE_DEFAULTS = {
  RECT: { width: 20, height: 4 },
  CIRCLE: { width: 20 },
  STAR: { width: 20, height: 20 },
  CARD: { width: 15, height: 20 },
  ARROW: { width: 35, height: 3 },
} as const;

const LAYER_SCALES = {
  GLOW: 1.6,
  INNER: 1.15,
  HIGHLIGHT: 0.35,
  HIGHLIGHT_ALPHA: 0.6,
  STAR_INNER_RATIO: 0.4,
  STAR_CENTER: 0.2,
  STAR_CENTER_ALPHA: 0.7,
  TRAIL_ALPHA: 0.25,
  GLOW_ALPHA: 0.15,
  INNER_ALPHA: 0.3,
  CARD_INNER_ALPHA: 0.3,
  ARROW_STREAK_ALPHA: 0.12,
  ARROW_GLOW_ALPHA: 0.2,
  ARROW_HEAD_RATIO: 0.25,
  ARROW_HEAD_HEIGHT: 2.5,
  ARROW_TIP_ALPHA: 0.7,
} as const;

// ── Types ────────────────────────────────────────────────────────

export interface ShapeConfig {
  width?: number;
  height?: number;
  strokeColor?: number;
  strokeWidth?: number;
}

export interface ProjectileShapeBuilder {
  (g: IGraphics, config: ShapeConfig, color: number): void;
}

// scene.add.rectangle uses CENTER (cx, cy) anchoring; my IGraphics.fillRect
// uses TOP-LEFT. Helper to bridge.
function fillCenteredRect(
  g: IGraphics,
  cx: number, cy: number,
  w: number, h: number,
  color: number, alpha = 1,
): void {
  g.fillRect(cx - w / 2, cy - h / 2, w, h, color, alpha);
}

function strokeCenteredRect(
  g: IGraphics,
  cx: number, cy: number,
  w: number, h: number,
  color: number, lineWidth: number, alpha = 1,
): void {
  g.strokeRect(cx - w / 2, cy - h / 2, w, h, color, lineWidth, alpha);
}

/** Rectangle — arrows, sword throws, piercing shots */
export const buildRect: ProjectileShapeBuilder = (g, config, color) => {
  const w = config.width || SHAPE_DEFAULTS.RECT.width;
  const h = config.height || SHAPE_DEFAULTS.RECT.height;

  // Trail — offset behind by 0.4w
  fillCenteredRect(g, -w * 0.4, 0, w * 0.8, h * 0.6, color, LAYER_SCALES.TRAIL_ALPHA);
  // Body
  fillCenteredRect(g, 0, 0, w, h, color, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    strokeCenteredRect(g, 0, 0, w, h, config.strokeColor, config.strokeWidth, 1);
  }
};

/** Circle — fireballs, frost bolts, arcane bolts */
export const buildCircle: ProjectileShapeBuilder = (g, config, color) => {
  const r = (config.width || SHAPE_DEFAULTS.CIRCLE.width) / 2;
  g.fillCircle(0, 0, r * LAYER_SCALES.GLOW, color, LAYER_SCALES.GLOW_ALPHA);
  g.fillCircle(0, 0, r * LAYER_SCALES.INNER, color, LAYER_SCALES.INNER_ALPHA);
  g.fillCircle(0, 0, r, color, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    g.strokeCircle(0, 0, r, config.strokeColor, config.strokeWidth, 1);
  }
  g.fillCircle(0, 0, r * LAYER_SCALES.HIGHLIGHT, 0xFFFFFF, LAYER_SCALES.HIGHLIGHT_ALPHA);
};

/** 5-pointed star — throw stars, lucky seven stars */
export const buildStar: ProjectileShapeBuilder = (g, config, color) => {
  const size = Math.max(config.width || 20, config.height || 20);
  const outerR = size / 2;
  const innerR = outerR * LAYER_SCALES.STAR_INNER_RATIO;
  const points = 5;

  g.fillCircle(0, 0, outerR * 1.4, color, LAYER_SCALES.GLOW_ALPHA);

  const verts = starPoints(0, 0, outerR, innerR, points);
  g.fillPolygon(verts, color, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    g.strokePolyline([...verts, verts[0]], config.strokeColor, config.strokeWidth, 1);
  }

  g.fillCircle(0, 0, outerR * LAYER_SCALES.STAR_CENTER, 0xFFFFFF, LAYER_SCALES.STAR_CENTER_ALPHA);
};

/** Card / diamond — lucky seven cards */
export const buildCard: ProjectileShapeBuilder = (g, config, color) => {
  const w = config.width || 15;
  const h = config.height || 20;
  const hw = w / 2;
  const hh = h / 2;

  g.fillCircle(0, 0, Math.max(hw, hh) * 1.3, color, LAYER_SCALES.GLOW_ALPHA);

  const diamond = [
    { x: 0, y: -hh },
    { x: hw, y: 0 },
    { x: 0, y: hh },
    { x: -hw, y: 0 },
  ];
  g.fillPolygon(diamond, color, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    g.strokePolyline([...diamond, diamond[0]], config.strokeColor, config.strokeWidth, 1);
  }

  const innerDiamond = [
    { x: 0, y: -hh * 0.5 },
    { x: hw * 0.5, y: 0 },
    { x: 0, y: hh * 0.5 },
    { x: -hw * 0.5, y: 0 },
  ];
  g.fillPolygon(innerDiamond, 0xFFFFFF, LAYER_SCALES.CARD_INNER_ALPHA);
};

/** Arrow — multi-layered arrow with arrowhead, shaft, glow, and tip highlight */
export const buildArrow: ProjectileShapeBuilder = (g, config, color) => {
  const w = config.width || SHAPE_DEFAULTS.ARROW.width;
  const h = config.height || SHAPE_DEFAULTS.ARROW.height;

  // Layer 1: Motion streak trail
  fillCenteredRect(g, -w * 0.6, 0, w * 1.2, h * 2.5, color, LAYER_SCALES.ARROW_STREAK_ALPHA);
  // Layer 2: Shaft glow halo
  fillCenteredRect(g, 0, 0, w * 1.1, h * 3, color, LAYER_SCALES.ARROW_GLOW_ALPHA);
  // Layer 3: Arrow shaft
  fillCenteredRect(g, -w * 0.1, 0, w * 0.7, h, color, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    strokeCenteredRect(g, -w * 0.1, 0, w * 0.7, h, config.strokeColor, config.strokeWidth, 1);
  }
  // Layer 4: Arrowhead triangles
  const tipX = w * 0.5;
  const headW = w * LAYER_SCALES.ARROW_HEAD_RATIO;
  const headH = h * LAYER_SCALES.ARROW_HEAD_HEIGHT;
  // Inner highlight (slightly larger, translucent white)
  g.fillPolygon([
    { x: tipX, y: 0 },
    { x: tipX - headW, y: -headH / 2 },
    { x: tipX - headW, y: headH / 2 },
  ], 0xFFFFFF, 0.3);
  // Solid arrowhead on top
  g.fillPolygon([
    { x: tipX, y: 0 },
    { x: tipX - headW * 0.8, y: -headH * 0.35 },
    { x: tipX - headW * 0.8, y: headH * 0.35 },
  ], color, 1);
  // Layer 5: Bright tip highlight
  g.fillCircle(tipX, 0, h * 0.8, 0xFFFFFF, LAYER_SCALES.ARROW_TIP_ALPHA);
};

/** Crescent — moon/scythe shape for slashing projectiles. Built as a
 * sampled polygon (outer arc + inner arc reversed), since the IGraphics
 * surface doesn't expose multi-arc paths.
 */
export const buildCrescent: ProjectileShapeBuilder = (g, config, color) => {
  const w = config.width || 20;
  const h = config.height || 20;
  const r = Math.max(w, h) / 2;

  g.fillCircle(0, 0, r * 1.4, color, LAYER_SCALES.GLOW_ALPHA);

  const verts: Array<{ x: number; y: number }> = [];
  const outerStart = -Math.PI * 0.7;
  const outerEnd = Math.PI * 0.7;
  const innerCx = r * 0.3;
  const innerR = r * 0.7;
  const SAMPLES = 24;
  // Outer arc (left-to-right)
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const a = outerStart + (outerEnd - outerStart) * t;
    verts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
  }
  // Inner arc (right-to-left), reversed so we close the polygon clean.
  for (let i = SAMPLES; i >= 0; i--) {
    const t = i / SAMPLES;
    const a = outerStart + (outerEnd - outerStart) * t;
    verts.push({ x: innerCx + Math.cos(a) * innerR, y: Math.sin(a) * innerR });
  }
  g.fillPolygon(verts, color, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    g.strokePolyline([...verts, verts[0]], config.strokeColor, config.strokeWidth, 1);
  }

  // Edge highlight
  g.fillCircle(-r * 0.3, 0, r * 0.2, 0xFFFFFF, 0.5);
};

/** Spiral — spinning spiral projectile. Caller is expected to apply
 * rotation via setRotation; the spinSpeed convention is owned by the
 * renderer (see ProjectileRenderer.spinSpeeds).
 */
export const buildSpiral: ProjectileShapeBuilder = (g, config, color) => {
  const size = Math.max(config.width || 20, config.height || 20);
  const r = size / 2;

  g.fillCircle(0, 0, r * 1.5, color, LAYER_SCALES.GLOW_ALPHA);

  // Spiral path via polyline
  const points: Array<{ x: number; y: number }> = [];
  const N = 40;
  const turns = 2;
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const angle = t * turns * Math.PI * 2;
    const sr = t * r;
    points.push({ x: Math.cos(angle) * sr, y: Math.sin(angle) * sr });
  }
  g.strokePolyline(points, color, 2, 1);

  g.fillCircle(0, 0, r * 0.2, 0xFFFFFF, 0.7);
};

/** Beam — straight beam for channel abilities */
export const buildBeam: ProjectileShapeBuilder = (g, config, color) => {
  const w = config.width || 60;
  const h = config.height || 6;

  fillCenteredRect(g, 0, 0, w * 1.3, h * 3, color, LAYER_SCALES.ARROW_STREAK_ALPHA);
  fillCenteredRect(g, 0, 0, w * 1.1, h * 1.8, color, LAYER_SCALES.TRAIL_ALPHA);
  fillCenteredRect(g, 0, 0, w, h, color, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    strokeCenteredRect(g, 0, 0, w, h, config.strokeColor, config.strokeWidth, 1);
  }
  fillCenteredRect(g, 0, 0, w * 0.8, h * 0.3, 0xFFFFFF, 0.6);
};

/** Returns flat array of star polygon vertices as {x,y} points */
/**
 * Hammer — Hammer of Wrath (plan 29 F9, lane BX 2026-10-11). A haft with a
 * broad head at one end and a soft glow, drawn once around the centre; the
 * renderer spins it (ProjectileRenderer `spinSpeed`) so it tumbles end over
 * end instead of flying as the square it used to be.
 */
export const buildHammer: ProjectileShapeBuilder = (g, config, color) => {
  const s = Math.max(config.width || 24, config.height || 24);
  g.fillCircle(0, 0, s * 0.7, color, LAYER_SCALES.GLOW_ALPHA);
  // Haft (dark wood) from the far end through the centre.
  fillCenteredRect(g, -s * 0.12, 0, s * 0.8, s * 0.14, 0x8a6a3a, 1);
  // Head: broad block at the leading end, bright face line.
  fillCenteredRect(g, s * 0.3, 0, s * 0.34, s * 0.62, color, 1);
  fillCenteredRect(g, s * 0.3, 0, s * 0.08, s * 0.5, 0xffffff, 0.85);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    strokeCenteredRect(g, s * 0.3, 0, s * 0.34, s * 0.62, config.strokeColor, config.strokeWidth, 1);
  }
};

/**
 * Blade — a thrown knife, point first (plan 29 F9, lane BY: Fan of Knives'
 * TOML `projectile_shape = "blade"` fell back to a star). A slim steel blade
 * with a bright edge, a crossguard and a dark grip, drawn once around the
 * centre; the renderer faces it along its velocity.
 */
export const buildBlade: ProjectileShapeBuilder = (g, config, color) => {
  const len = config.width || 22;
  const t = Math.max(3, len * 0.16);
  g.fillCircle(0, 0, len * 0.5, color, LAYER_SCALES.GLOW_ALPHA * 1.6);
  g.fillPolygon([
    { x: len * 0.5, y: 0 },
    { x: -len * 0.08, y: -t },
    { x: -len * 0.08, y: t },
  ], color, 1);
  g.fillPolygon([
    { x: len * 0.5, y: 0 },
    { x: -len * 0.08, y: -t * 0.35 },
    { x: -len * 0.08, y: 0 },
  ], 0xFFFFFF, 0.85);
  fillCenteredRect(g, -len * 0.1, 0, 3, t * 2.6, 0x9a9aa8, 1);
  fillCenteredRect(g, -len * 0.3, 0, len * 0.36, t * 0.9, 0x3a2a22, 1);
  if (config.strokeColor !== undefined && config.strokeWidth !== undefined) {
    strokeCenteredRect(g, len * 0.2, 0, len * 0.6, t * 2, config.strokeColor, config.strokeWidth, 1);
  }
};

export function starPoints(
  cx: number, cy: number,
  outerR: number, innerR: number,
  points: number,
): Array<{ x: number; y: number }> {
  const result: Array<{ x: number; y: number }> = [];
  const step = Math.PI / points;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = -Math.PI / 2 + i * step;
    result.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
  }
  return result;
}
